import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const CHART_COLORS = [
    '#6366F1', // Indigo
    '#22C55E', // Green
    '#F97316', // Orange
    '#EC4899', // Pink
    '#0EA5E9', // Sky
    '#F59E0B', // Amber
    '#8B5CF6', // Purple
    '#14B8A6', // Teal
    '#EF4444', // Red
    '#10B981', // Emerald
];

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '¥0';
    return `¥${value.toLocaleString('ja-JP')}`;
};

export default function AccountingAnalytics({ onOpenUnrepaidList }) {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // 日期筛选
    const initialDateFilter = { startMonth: '', endMonth: '' };
    const [dateFilters, setDateFilters] = useState(initialDateFilter);
    const [pendingDateFilters, setPendingDateFilters] = useState(initialDateFilter);
    const [dateValidationError, setDateValidationError] = useState(null);

    // 图表选项状态（从localStorage读取）
    const STORAGE_KEY = 'accounting_analytics_chart_preferences';
    const getStoredPreferences = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {
                lineChart: 'loan_trend',        // 折线图：借款趋势
                pieChart: 'repayment_status',   // 饼图：还款状态
                barChart1: 'monthly_loan',      // 柱状图1：月度借款
                barChart2: 'monthly_repayment'  // 柱状图2：月度还款
            };
        } catch {
            return {
                lineChart: 'loan_trend',
                pieChart: 'repayment_status',
                barChart1: 'monthly_loan',
                barChart2: 'monthly_repayment'
            };
        }
    };

    const [chartOptions, setChartOptions] = useState(getStoredPreferences());

    // 保存图表选项到localStorage
    const updateChartOption = (chartType, value) => {
        const newOptions = { ...chartOptions, [chartType]: value };
        setChartOptions(newOptions);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newOptions));
    };

    useEffect(() => {
        let isMounted = true;
        const loadAnalytics = async () => {
            setIsLoading(true);
            try {
                const data = await apiService.getAccountingAnalytics({
                    startMonth: dateFilters.startMonth || undefined,
                    endMonth: dateFilters.endMonth || undefined
                });
                if (!isMounted) return;
                setAnalytics(data);
                setError(null);
            } catch (err) {
                if (!isMounted) return;
                console.error('Failed to load accounting analytics', err);
                setError(err?.message || t('accountingAnalytics.loadError'));
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadAnalytics();

        return () => {
            isMounted = false;
        };
    }, [t, dateFilters.startMonth, dateFilters.endMonth]);

    const handleApplyDateFilters = () => {
        setDateValidationError(null);

        if (pendingDateFilters.startMonth && pendingDateFilters.endMonth) {
            if (pendingDateFilters.startMonth > pendingDateFilters.endMonth) {
                setDateValidationError(t('accountingAnalytics.dateRangeError'));
                return;
            }
        }

        setDateFilters(pendingDateFilters);
    };

    const handleResetDateFilters = () => {
        setPendingDateFilters(initialDateFilter);
        setDateFilters(initialDateFilter);
        setDateValidationError(null);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                    <div className="h-64 bg-slate-200 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('accountingAnalytics.title')}</h3>
                <p className="text-sm text-red-600">{t('accountingAnalytics.errorOccurred')}: {error}</p>
            </div>
        );
    }

    if (!analytics) {
        return null;
    }

    const totalBorrowersCount = Number.isFinite(Number(analytics.totalBorrowers))
        ? Number(analytics.totalBorrowers)
        : 0;
    const fullyRepaidBorrowersCount = Number.isFinite(Number(analytics.fullyRepaidCount))
        ? Number(analytics.fullyRepaidCount)
        : 0;
    const unrepaidCountFromApi = Number(analytics.unrepaidCount);
    const unrepaidCount = Number.isFinite(unrepaidCountFromApi)
        ? Math.max(0, unrepaidCountFromApi)
        : Math.max(0, totalBorrowersCount - fullyRepaidBorrowersCount);

    // 关键指标卡片数据
    const keyMetrics = [
        {
            label: t('accountingAnalytics.totalLoanAmount'),
            value: formatCurrency(analytics.totalLoanAmount),
            color: 'bg-blue-50 border-blue-200'
        },
        {
            label: t('accountingAnalytics.rentalLoanAmount'),
            value: formatCurrency(analytics.rentalLoanAmount),
            color: 'bg-amber-50 border-amber-200'
        },
        {
            label: t('accountingAnalytics.advanceLoanAmount'),
            value: formatCurrency(analytics.advanceLoanAmount),
            color: 'bg-sky-50 border-sky-200'
        },
        {
            label: t('accountingAnalytics.totalRepaymentAmount'),
            value: formatCurrency(analytics.totalRepaymentAmount),
            color: 'bg-green-50 border-green-200'
        },
        {
            label: t('accountingAnalytics.remainingBalance'),
            value: formatCurrency(analytics.remainingBalance),
            color: 'bg-orange-50 border-orange-200'
        },
        {
            label: t('accountingAnalytics.totalBorrowers'),
            value: `${analytics.totalBorrowers}人`,
            color: 'bg-purple-50 border-purple-200'
        },
        {
            label: t('accountingAnalytics.unrepaidCount'),
            value: `${unrepaidCount}人`,
            color: 'bg-emerald-50 border-emerald-200',
            onClick: onOpenUnrepaidList
        },
        {
            label: t('accountingAnalytics.averageLoanAmount'),
            value: formatCurrency(analytics.averageLoanAmount),
            color: 'bg-indigo-50 border-indigo-200'
        }
    ];

    // 折线图选项
    const lineChartOptions = {
        loan_trend: {
            title: t('accountingAnalytics.loanTrend'),
            data: {
                labels: analytics.monthlyLoanTrend?.map(item => item.month) || [],
                datasets: [{
                    label: t('accountingAnalytics.loanAmount'),
                    data: analytics.monthlyLoanTrend?.map(item => item.amount) || [],
                    borderColor: CHART_COLORS[0],
                    backgroundColor: CHART_COLORS[0] + '20',
                    fill: true,
                    tension: 0.4
                }]
            }
        },
        repayment_trend: {
            title: t('accountingAnalytics.repaymentTrend'),
            data: {
                labels: analytics.monthlyRepaymentTrend?.map(item => item.month) || [],
                datasets: [{
                    label: t('accountingAnalytics.repaymentAmount'),
                    data: analytics.monthlyRepaymentTrend?.map(item => item.amount) || [],
                    borderColor: CHART_COLORS[1],
                    backgroundColor: CHART_COLORS[1] + '20',
                    fill: true,
                    tension: 0.4
                }]
            }
        },
        combined_trend: {
            title: t('accountingAnalytics.combinedTrend'),
            data: {
                labels: analytics.monthlyLoanTrend?.map(item => item.month) || [],
                datasets: [
                    {
                        label: t('accountingAnalytics.loanAmount'),
                        data: analytics.monthlyLoanTrend?.map(item => item.amount) || [],
                        borderColor: CHART_COLORS[0],
                        backgroundColor: CHART_COLORS[0] + '20',
                        fill: true,
                        tension: 0.4
                    },
                    {
                        label: t('accountingAnalytics.repaymentAmount'),
                        data: analytics.monthlyRepaymentTrend?.map(item => item.amount) || [],
                        borderColor: CHART_COLORS[1],
                        backgroundColor: CHART_COLORS[1] + '20',
                        fill: true,
                        tension: 0.4
                    }
                ]
            }
        }
    };

    // 饼图选项
    const pieChartOptions = {
        repayment_status: {
            title: t('accountingAnalytics.repaymentStatusDistribution'),
            data: {
                labels: analytics.repaymentStatusDistribution?.map(item => item.status) || [],
                datasets: [{
                    data: analytics.repaymentStatusDistribution?.map(item => item.count) || [],
                    backgroundColor: CHART_COLORS
                }]
            }
        },
        category_distribution: {
            title: t('accountingAnalytics.categoryDistribution'),
            data: {
                labels: analytics.categoryDistribution?.map(item => item.category || '他') || [],
                datasets: [{
                    data: analytics.categoryDistribution?.map(item => item.amount) || [],
                    backgroundColor: CHART_COLORS
                }]
            }
        },
        organization_distribution: {
            title: t('accountingAnalytics.organizationDistribution'),
            data: {
                labels: analytics.organizationDistribution?.map(item => item.organization || '未設定') || [],
                datasets: [{
                    data: analytics.organizationDistribution?.map(item => item.count) || [],
                    backgroundColor: CHART_COLORS
                }]
            }
        },
        sales_staff_distribution: {
            title: t('accountingAnalytics.salesStaffDistribution'),
            data: {
                labels: analytics.salesStaffDistribution?.map(item => item.salesStaff || '未設定') || [],
                datasets: [{
                    data: analytics.salesStaffDistribution?.map(item => item.count) || [],
                    backgroundColor: CHART_COLORS
                }]
            }
        }
    };

    // 柱状图选项
    const barChartOptions = {
        monthly_loan: {
            title: t('accountingAnalytics.monthlyLoan'),
            data: {
                labels: analytics.monthlyLoanStats?.map(item => item.month) || [],
                datasets: [{
                    label: t('accountingAnalytics.loanAmount'),
                    data: analytics.monthlyLoanStats?.map(item => item.amount) || [],
                    backgroundColor: CHART_COLORS[0]
                }]
            }
        },
        monthly_repayment: {
            title: t('accountingAnalytics.monthlyRepayment'),
            data: {
                labels: analytics.monthlyRepaymentStats?.map(item => item.month) || [],
                datasets: [{
                    label: t('accountingAnalytics.repaymentAmount'),
                    data: analytics.monthlyRepaymentStats?.map(item => item.amount) || [],
                    backgroundColor: CHART_COLORS[1]
                }]
            }
        },
        quarterly_comparison: {
            title: t('accountingAnalytics.quarterlyComparison'),
            data: {
                labels: analytics.quarterlyStats?.map(item => item.quarter) || [],
                datasets: [
                    {
                        label: t('accountingAnalytics.loanAmount'),
                        data: analytics.quarterlyStats?.map(item => item.loanAmount) || [],
                        backgroundColor: CHART_COLORS[0]
                    },
                    {
                        label: t('accountingAnalytics.repaymentAmount'),
                        data: analytics.quarterlyStats?.map(item => item.repaymentAmount) || [],
                        backgroundColor: CHART_COLORS[1]
                    }
                ]
            }
        }
    };

    const currentLineChart = lineChartOptions[chartOptions.lineChart];
    const currentPieChart = pieChartOptions[chartOptions.pieChart];
    const currentBarChart1 = barChartOptions[chartOptions.barChart1];
    const currentBarChart2 = barChartOptions[chartOptions.barChart2];

    return (
        <div className="space-y-6">
            {/* 日期筛选 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('accountingAnalytics.dateFilter')}</h3>
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('accountingAnalytics.startMonth')}
                        </label>
                        <input
                            type="month"
                            value={pendingDateFilters.startMonth}
                            onChange={(e) => setPendingDateFilters({ ...pendingDateFilters, startMonth: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('accountingAnalytics.endMonth')}
                        </label>
                        <input
                            type="month"
                            value={pendingDateFilters.endMonth}
                            onChange={(e) => setPendingDateFilters({ ...pendingDateFilters, endMonth: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleApplyDateFilters}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            {t('accountingAnalytics.apply')}
                        </button>
                        <button
                            onClick={handleResetDateFilters}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                            {t('accountingAnalytics.reset')}
                        </button>
                    </div>
                </div>
                {dateValidationError && (
                    <p className="mt-2 text-sm text-red-600">{dateValidationError}</p>
                )}
            </div>

            {/* 关键指标卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {keyMetrics.map((metric, index) => (
                    metric.onClick ? (
                        <button
                            key={index}
                            type="button"
                            onClick={metric.onClick}
                            className={`w-full text-left bg-white rounded-xl shadow-lg p-6 border-2 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[var(--primary-300)] ${metric.color}`}
                        >
                            <p className="text-sm font-medium text-gray-600 mb-1">{metric.label}</p>
                            <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                        </button>
                    ) : (
                        <div key={index} className={`bg-white rounded-xl shadow-lg p-6 border-2 ${metric.color}`}>
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">{metric.label}</p>
                                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                            </div>
                        </div>
                    )
                ))}
            </div>

            {/* 折线图 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{currentLineChart.title}</h3>
                    <select
                        value={chartOptions.lineChart}
                        onChange={(e) => updateChartOption('lineChart', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="loan_trend">{t('accountingAnalytics.loanTrend')}</option>
                        <option value="repayment_trend">{t('accountingAnalytics.repaymentTrend')}</option>
                        <option value="combined_trend">{t('accountingAnalytics.combinedTrend')}</option>
                    </select>
                </div>
                <div className="h-64">
                    <Line
                        data={currentLineChart.data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: true, position: 'top' },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
                                    }
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: (value) => formatCurrency(value)
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </div>

            {/* 饼图 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{currentPieChart.title}</h3>
                    <select
                        value={chartOptions.pieChart}
                        onChange={(e) => updateChartOption('pieChart', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="repayment_status">{t('accountingAnalytics.repaymentStatusDistribution')}</option>
                        <option value="category_distribution">{t('accountingAnalytics.categoryDistribution')}</option>
                        <option value="organization_distribution">{t('accountingAnalytics.organizationDistribution')}</option>
                        <option value="sales_staff_distribution">{t('accountingAnalytics.salesStaffDistribution')}</option>
                    </select>
                </div>
                <div className="h-64">
                    <Pie
                        data={currentPieChart.data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { position: 'right' }
                            }
                        }}
                    />
                </div>
            </div>

            {/* 柱状图1 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{currentBarChart1.title}</h3>
                    <select
                        value={chartOptions.barChart1}
                        onChange={(e) => updateChartOption('barChart1', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="monthly_loan">{t('accountingAnalytics.monthlyLoan')}</option>
                        <option value="monthly_repayment">{t('accountingAnalytics.monthlyRepayment')}</option>
                        <option value="quarterly_comparison">{t('accountingAnalytics.quarterlyComparison')}</option>
                    </select>
                </div>
                <div className="h-64">
                    <Bar
                        data={currentBarChart1.data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: true, position: 'top' },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
                                    }
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: (value) => formatCurrency(value)
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </div>

            {/* 柱状图2 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{currentBarChart2.title}</h3>
                    <select
                        value={chartOptions.barChart2}
                        onChange={(e) => updateChartOption('barChart2', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="monthly_loan">{t('accountingAnalytics.monthlyLoan')}</option>
                        <option value="monthly_repayment">{t('accountingAnalytics.monthlyRepayment')}</option>
                        <option value="quarterly_comparison">{t('accountingAnalytics.quarterlyComparison')}</option>
                    </select>
                </div>
                <div className="h-64">
                    <Bar
                        data={currentBarChart2.data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: true, position: 'top' },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
                                    }
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: (value) => formatCurrency(value)
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
